'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocale, useTranslations } from 'next-intl';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button/button';
import { Input } from '@/components/ui/input/input';
import { Label } from '@/components/ui/label/label';

const forgotPasswordSchema = z.object({
  email: z.string().email(),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

type ForgotPasswordState = 'idle' | 'submitting' | 'sent' | 'error';

export function ForgotPasswordPage() {
  const t = useTranslations('auth.forgotPassword');
  const locale = useLocale();
  const localePath = (path: string) => (locale === 'en' ? path : `/${locale}${path}`);
  const [state, setState] = useState<ForgotPasswordState>('idle');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setState('submitting');
    try {
      const res = await fetch('/api/auth/request-password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email, redirectTo: localePath('/reset-password') }),
        credentials: 'include',
      });
      // Always show success to avoid leaking whether an account exists
      setState(res.ok ? 'sent' : 'sent');
    } catch {
      setState('error');
    }
  };

  return (
    <div
      className="notranslate flex min-h-[80vh] items-center justify-center bg-stone-950 p-6"
      translate="no"
    >
      <div className="max-w-md w-full rounded-xl border border-stone-800 bg-stone-900 p-8">
        {state === 'sent' ? (
          <div className="flex flex-col items-center gap-3 text-center">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
            <h1 className="text-xl font-semibold text-white">{t('sentTitle')}</h1>
            <p className="text-stone-400 text-sm">{t('sentDescription')}</p>
            <Button asChild variant="outline" className="mt-4">
              <a href={localePath('/login')}>{t('backToLogin')}</a>
            </Button>
          </div>
        ) : (
          <>
            <h1 className="text-xl font-semibold text-white mb-2">{t('title')}</h1>
            <p className="text-stone-400 text-sm mb-6">{t('description')}</p>

            <form
              onSubmit={(e) => {
                void handleSubmit(onSubmit)(e);
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="email" className="text-stone-300">
                  {t('emailLabel')}
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  {...register('email')}
                  disabled={state === 'submitting'}
                  className="bg-stone-800 border-stone-700 text-white"
                />
                {errors.email && <p className="text-sm text-red-400">{errors.email.message}</p>}
              </div>

              {state === 'error' && (
                <div className="rounded-md bg-red-900/20 p-3 text-sm text-red-400">
                  {t('error')}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={state === 'submitting'}>
                {state === 'submitting' ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('sending')}
                  </>
                ) : (
                  t('submit')
                )}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <a href={localePath('/login')} className="text-stone-400 text-sm hover:text-white">
                {t('backToLogin')}
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
